import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Search, ChevronLeft, ChevronRight, FlaskConical, Trash2, TestTube2, Calendar, CheckCircle, XCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { DeletionRequestsView, RecycleBinView } from '../components/DeletionRequestsPanel';

const statusColors = {
  pending: 'bg-amber-100 text-amber-700',
  under_review: 'bg-blue-100 text-blue-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
  needs_revision: 'bg-orange-100 text-orange-700',
};

const priorityColors = {
  high: 'bg-red-100 text-red-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-slate-100 text-slate-600',
};

export default function SubmissionsPage() {
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [formFilter, setFormFilter] = useState('all');
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [monthPickerOpen, setMonthPickerOpen] = useState(false);
  const [formTypes, setFormTypes] = useState([]);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTest, setShowTest] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkAction, setBulkAction] = useState('');
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [view, setView] = useState('list');
  const [pendingIds, setPendingIds] = useState(new Set());
  const [pendingCount, setPendingCount] = useState(0);
  const [requestModal, setRequestModal] = useState(null); // {ids: []}
  const [requestReason, setRequestReason] = useState('');
  const navigate = useNavigate();

  const canManage = user?.role === 'admin' || user?.role === 'manager';
  const isAdmin = user?.role === 'admin';

  const fetchPending = useCallback(async () => {
    try {
      const r = await api.get('/deletion-requests/pending-ids');
      setPendingIds(new Set(r.data.ids));
      setPendingCount(r.data.pending_count);
    } catch { }
  }, []);
  useEffect(() => { if (canManage) fetchPending(); }, [canManage, fetchPending]);

  const submitRequest = async () => {
    try {
      const r = await api.post('/deletion-requests', { ids: requestModal.ids, reason: requestReason });
      toast.success(r.data.message);
      setRequestModal(null); setRequestReason(''); setSelected(new Set());
      fetchPending();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to submit request'); }
  };

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    setSelected(new Set());
    try {
      const params = { page, page_size: 15, show_test: showTest };
      if (search) params.search = search;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (typeFilter !== 'all') params.form_type = typeFilter;
      if (formFilter !== 'all') params.form_id = formFilter;
      if (selectedMonths.length > 0) params.months = selectedMonths.join(',');
      const res = await api.get('/submissions', { params });
      setSubmissions(res.data.submissions);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
    } catch {
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, typeFilter, formFilter, selectedMonths, showTest]);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);
  useEffect(() => {
    api.get('/form-types').then(r => setFormTypes(r.data)).catch(() => {});
    api.get('/form-dashboard/cards').then(r => setForms(r.data)).catch(() => {});
  }, []);

  const toggleSelect = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };

  const toggleSelectAll = () => {
    if (selected.size === submissions.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(submissions.map(s => s.id)));
    }
  };

  const handleBulkConfirm = async () => {
    const ids = Array.from(selected);
    try {
      const res = await api.post('/submissions/bulk-action', { action: bulkAction, ids });
      toast.success(res.data.message);
      setConfirmBulk(false);
      setBulkAction('');
      fetchSubmissions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk action failed');
    }
  };

  const handleDeleteOne = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/submissions/${confirmDelete.id}`);
      toast.success('Submission deleted');
      setConfirmDelete(null);
      fetchSubmissions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleMarkTestOne = async (sub) => {
    const action = sub.is_test ? 'unmark_test' : 'mark_test';
    try {
      await api.post('/submissions/bulk-action', { action, ids: [sub.id] });
      toast.success(sub.is_test ? 'Unmarked as test' : 'Marked as test');
      fetchSubmissions();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleApproveOne = async (sub) => {
    try {
      await api.post(`/submissions/${sub.id}/review`, { action: 'approved', comment: '' });
      toast.success('Submission approved');
      fetchSubmissions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleRejectOne = async (sub) => {
    try {
      await api.post(`/submissions/${sub.id}/review`, { action: 'rejected', comment: '' });
      toast.success('Submission rejected');
      fetchSubmissions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    }
  };

  const bulkActionLabel = {
    approve: 'Approve',
    reject: 'Reject',
    mark_test: 'Mark as Test',
    unmark_test: 'Unmark as Test',
    delete: 'Delete',
  };

  return (
    <div data-testid="submissions-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Submissions</h1>
          <p className="text-sm text-muted-foreground mt-1">{total} {showTest ? 'total' : 'real'} submission{total !== 1 ? 's' : ''}</p>
        </div>
        <Button
          variant={showTest ? 'default' : 'outline'}
          size="sm"
          className={`rounded-sm text-xs gap-1.5 ${showTest ? 'bg-amber-500 hover:bg-amber-600 text-white border-amber-500' : 'text-muted-foreground'}`}
          onClick={() => { setShowTest(v => !v); setPage(1); }}
          data-testid="show-test-toggle"
        >
          <FlaskConical className="w-3.5 h-3.5" />
          {showTest ? 'Hiding Live Data' : 'Show Test Data'}
        </Button>
      </div>

      {/* View switcher */}
      {canManage && (
        <div className="flex items-center gap-2 mb-4" data-testid="submissions-view-switcher">
          {[['list', 'Submissions'], ['requests', `Deletion Requests${pendingCount ? ` (${pendingCount})` : ''}`], ...(isAdmin ? [['bin', 'Recycle Bin']] : [])].map(([v, label]) => (
            <button key={v} type="button" onClick={() => setView(v)} data-testid={`submissions-view-${v}`}
              className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${view === v ? 'bg-slate-900 text-white border-slate-900' : v === 'requests' && pendingCount ? 'border-amber-300 text-amber-700 bg-amber-50' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>{label}</button>
          ))}
        </div>
      )}

      {view === 'requests' && <DeletionRequestsView isAdmin={isAdmin} onChanged={() => { fetchPending(); fetchSubmissions(); }} />}
      {view === 'bin' && isAdmin && <RecycleBinView onChanged={fetchSubmissions} />}

      {view === 'list' && (<>
      {/* Filters */}
      <Card className="border border-border rounded-sm shadow-sm mb-4">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                data-testid="submissions-search"
                placeholder="Search by name or form..."
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                className="pl-9 h-9 rounded-sm"
              />
            </div>
            <Select value={formFilter} onValueChange={v => { setFormFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[220px] h-9 rounded-sm" data-testid="form-filter">
                <SelectValue placeholder="All Forms" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Forms</SelectItem>
                {forms.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[140px] h-9 rounded-sm" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="under_review">Under Review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="needs_revision">Needs Revision</SelectItem>
              </SelectContent>
            </Select>
            {/* Month Multi-Select */}
            <div className="relative">
              <button
                onClick={() => setMonthPickerOpen(v => !v)}
                data-testid="month-filter-btn"
                className={`flex items-center gap-2 h-9 px-3 rounded-sm border text-xs transition-colors ${selectedMonths.length > 0 ? 'bg-slate-900 text-white border-slate-900' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}
              >
                <Calendar className="w-3.5 h-3.5" />
                {selectedMonths.length === 0 ? 'All Months' : `${selectedMonths.length} month${selectedMonths.length > 1 ? 's' : ''}`}
              </button>
              {monthPickerOpen && (
                <div className="absolute top-full mt-1 right-0 z-50 bg-white border border-slate-200 rounded-sm shadow-lg p-3 w-[280px]" data-testid="month-picker-dropdown">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Select Months</span>
                    {selectedMonths.length > 0 && (
                      <button onClick={() => { setSelectedMonths([]); setPage(1); }} className="text-[10px] text-red-500 hover:text-red-700 font-medium">Clear all</button>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-1.5 max-h-[220px] overflow-y-auto">
                    {(() => {
                      const now = new Date();
                      const months = [];
                      for (let i = 11; i >= 0; i--) {
                        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                        const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                        const label = d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                        months.push({ val, label });
                      }
                      return months.map(m => {
                        const isSelected = selectedMonths.includes(m.val);
                        return (
                          <button key={m.val} data-testid={`month-${m.val}`}
                            onClick={() => {
                              setSelectedMonths(prev => prev.includes(m.val) ? prev.filter(v => v !== m.val) : [...prev, m.val]);
                              setPage(1);
                            }}
                            className={`px-2 py-1.5 rounded-sm text-[11px] font-medium transition-colors border ${isSelected ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                          >
                            {m.label}
                          </button>
                        );
                      });
                    })()}
                  </div>
                  <button onClick={() => setMonthPickerOpen(false)} className="w-full mt-2 text-xs text-center py-1.5 bg-slate-50 hover:bg-slate-100 rounded-sm text-slate-600 font-medium transition-colors">Done</button>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions Bar */}
      {canManage && selected.size > 0 && (
        <div className="flex items-center gap-3 mb-3 px-4 py-2.5 bg-blue-50 border border-blue-200 rounded-sm" data-testid="bulk-actions-bar">
          <span className="text-xs font-medium text-blue-800">{selected.size} selected</span>
          <div className="flex gap-2 ml-auto">
            <Button size="sm" className="rounded-sm text-xs h-7 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => { setBulkAction('approve'); setConfirmBulk(true); }} data-testid="bulk-approve-btn">
              <CheckCircle className="w-3 h-3 mr-1" /> Approve
            </Button>
            <Button size="sm" variant="outline" className="rounded-sm text-xs h-7 text-red-600 border-red-200 hover:bg-red-50" onClick={() => { setBulkAction('reject'); setConfirmBulk(true); }} data-testid="bulk-reject-btn">
              <XCircle className="w-3 h-3 mr-1" /> Reject
            </Button>
            <Button size="sm" variant="outline" className="rounded-sm text-xs h-7" onClick={() => { setBulkAction('mark_test'); setConfirmBulk(true); }} data-testid="bulk-mark-test-btn">
              <FlaskConical className="w-3 h-3 mr-1" /> Mark as Test
            </Button>
            <Button size="sm" variant="outline" className="rounded-sm text-xs h-7" onClick={() => { setBulkAction('unmark_test'); setConfirmBulk(true); }} data-testid="bulk-unmark-test-btn">
              <TestTube2 className="w-3 h-3 mr-1" /> Unmark Test
            </Button>
            <Button size="sm" variant="outline" className="rounded-sm text-xs h-7 text-red-600 border-red-200 hover:bg-red-50"
              onClick={() => { if (isAdmin) { setBulkAction('delete'); setConfirmBulk(true); } else { setRequestModal({ ids: Array.from(selected) }); } }} data-testid="bulk-delete-btn">
              <Trash2 className="w-3 h-3 mr-1" /> {isAdmin ? 'Delete' : 'Request deletion'}
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <Card className="border border-border rounded-sm shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-40"><p className="font-data text-sm text-muted-foreground">Loading...</p></div>
          ) : submissions.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40">
              <p className="text-sm text-muted-foreground">No submissions found</p>
              <p className="text-xs text-muted-foreground mt-1">{showTest ? 'No test submissions yet' : 'Submissions will appear here when received via webhooks'}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {canManage && (
                    <TableHead className="w-10 pl-4">
                      <Checkbox
                        checked={selected.size === submissions.length && submissions.length > 0}
                        onCheckedChange={toggleSelectAll}
                        data-testid="select-all-checkbox"
                        aria-label="Select all"
                      />
                    </TableHead>
                  )}
                  <TableHead className="text-[10px] uppercase tracking-wider">Form</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider">Type</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider">Status</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider">Received</TableHead>
                  {canManage && <TableHead className="w-32 text-[10px] uppercase tracking-wider">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {submissions.map(s => (
                  <TableRow
                    key={s.id}
                    data-testid={`submission-row-${s.id}`}
                    className={`transition-colors duration-150 ${s.is_test ? 'bg-amber-50/50' : ''} ${selected.has(s.id) ? 'bg-blue-50' : 'hover:bg-muted/50'}`}
                  >
                    {canManage && (
                      <TableCell className="pl-4" onClick={e => e.stopPropagation()}>
                        <Checkbox
                          checked={selected.has(s.id)}
                          onCheckedChange={() => toggleSelect(s.id)}
                          data-testid={`select-${s.id}`}
                          aria-label="Select row"
                        />
                      </TableCell>
                    )}
                    <TableCell className="font-medium text-sm cursor-pointer" onClick={() => navigate(`/submissions/${s.id}`)}>
                      <div className="flex items-center gap-1.5">
                        {s.form_name}
                        {s.is_test && <Badge className="text-[9px] rounded-sm bg-amber-100 text-amber-700 border border-amber-200 px-1 py-0 h-4">TEST</Badge>}
                        {pendingIds.has(s.id) && <Badge className="text-[9px] rounded-sm bg-orange-100 text-orange-700 border border-orange-200 px-1 py-0 h-4" data-testid={`pending-deletion-badge-${s.id}`}>DELETION REQUESTED</Badge>}
                      </div>
                    </TableCell>
                    <TableCell className="cursor-pointer" onClick={() => navigate(`/submissions/${s.id}`)}>
                      <Badge variant="outline" className="text-[10px] rounded-sm">{s.form_type}</Badge>
                    </TableCell>
                    <TableCell className="cursor-pointer" onClick={() => navigate(`/submissions/${s.id}`)}>
                      <Badge variant="secondary" className={`text-[10px] rounded-sm ${statusColors[s.status] || ''}`}>{s.status?.replace('_', ' ')}</Badge>
                    </TableCell>
                    <TableCell className="font-data text-xs text-muted-foreground cursor-pointer" onClick={() => navigate(`/submissions/${s.id}`)}>{formatDistanceToNow(new Date(s.created_at), { addSuffix: true })}</TableCell>
                    {canManage && (
                      <TableCell onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          {s.status === 'pending' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 rounded-sm text-emerald-600 hover:bg-emerald-50"
                                onClick={() => handleApproveOne(s)}
                                title="Approve"
                                data-testid={`approve-${s.id}`}
                              >
                                <CheckCircle className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 rounded-sm text-red-500 hover:bg-red-50"
                                onClick={() => handleRejectOne(s)}
                                title="Reject"
                                data-testid={`reject-${s.id}`}
                              >
                                <XCircle className="w-3.5 h-3.5" />
                              </Button>
                            </>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className={`h-7 w-7 p-0 rounded-sm ${s.is_test ? 'text-amber-600 hover:bg-amber-50' : 'text-muted-foreground hover:bg-slate-100'}`}
                            onClick={() => handleMarkTestOne(s)}
                            title={s.is_test ? 'Unmark as test' : 'Mark as test'}
                            data-testid={`mark-test-${s.id}`}
                          >
                            <FlaskConical className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 rounded-sm text-muted-foreground hover:text-red-600 hover:bg-red-50"
                            onClick={() => isAdmin ? setConfirmDelete(s) : setRequestModal({ ids: [s.id] })}
                            title={isAdmin ? 'Delete submission (moves to recycle bin)' : 'Request deletion (admin approval required)'}
                            data-testid={`delete-${s.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-xs text-muted-foreground">Page {page} of {totalPages}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="prev-page" className="rounded-sm">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="next-page" className="rounded-sm">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
      </>)}

      {/* Deletion Request Modal (managers) */}
      <AlertDialog open={!!requestModal} onOpenChange={open => { if (!open) { setRequestModal(null); setRequestReason(''); } }}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Request Deletion</AlertDialogTitle>
            <AlertDialogDescription>
              Deleting submissions requires <strong>admin approval</strong>. Your request for {requestModal?.ids.length} submission(s) will appear in the admin's Deletion Requests inbox. Please give a reason.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <textarea
            value={requestReason}
            onChange={e => setRequestReason(e.target.value)}
            placeholder="Why should this be deleted? (required)"
            className="w-full text-sm border border-slate-200 rounded-sm px-2.5 py-2 min-h-[80px]"
            data-testid="deletion-reason-input"
          />
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="submit-deletion-request-btn"
              className="rounded-sm bg-amber-600 hover:bg-amber-700 text-white"
              disabled={!requestReason.trim()}
              onClick={submitRequest}
            >
              Submit request
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Action Confirm */}
      <AlertDialog open={confirmBulk} onOpenChange={open => { if (!open) setConfirmBulk(false); }}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Confirm Bulk Action</AlertDialogTitle>
            <AlertDialogDescription>
              {bulkAction === 'delete'
                ? `Move ${selected.size} submission(s) to the Recycle Bin? They can be restored for 30 days.`
                : `${bulkActionLabel[bulkAction]} for ${selected.size} submission(s)?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" onClick={() => setConfirmBulk(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-bulk-action-btn"
              className={`rounded-sm ${bulkAction === 'delete' ? 'bg-red-600 hover:bg-red-700 text-white' : ''}`}
              onClick={handleBulkConfirm}
            >
              {bulkActionLabel[bulkAction] || 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Single Delete Confirm */}
      <AlertDialog open={!!confirmDelete} onOpenChange={open => { if (!open) setConfirmDelete(null); }}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete Submission</AlertDialogTitle>
            <AlertDialogDescription>
              Move this submission from <strong>{confirmDelete?.form_name}</strong> to the Recycle Bin? It can be restored for 30 days.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-submission-btn"
              className="rounded-sm bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDeleteOne}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
